import { fetchAuthSession } from '@aws-amplify/auth';
import type { ApiResponse, ApiError } from '@choco-admin/types';
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios';

import { getCurrentEnvironmentConfig } from '../environments/utils';

/**
 * Extended Axios request config with metadata
 */
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  metadata?: {
    startTime: number;
  };
}

/**
 * API client configuration
 */
interface ApiClientConfig {
  baseURL: string;
  timeout?: number;
  headers?: Record<string, string>;
}

/**
 * Extended Axios error with our API error format
 */
export interface ApiClientError extends AxiosError {
  apiErrors?: ApiError[];
  isAuthError?: boolean;
  isNetworkError?: boolean;
}

/**
 * Auth-aware API client class
 */
class ApiClient {
  private axiosInstance: AxiosInstance;
  private readonly baseURL: string;

  constructor(config: ApiClientConfig) {
    this.baseURL = config.baseURL;
    this.axiosInstance = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout || 30000,
      headers: {
        'Content-Type': 'application/json',
        ...config.headers,
      },
    });

    this.setupInterceptors();
  }

  /**
   * Setup request and response interceptors
   */
  private setupInterceptors() {
    // Request interceptor to add auth token
    this.axiosInstance.interceptors.request.use(
      async (config: ExtendedAxiosRequestConfig) => {
        try {
          // Get the current auth session
          const session = await fetchAuthSession();
          const accessToken = session.tokens?.accessToken?.toString();
          const idToken = session.tokens?.idToken?.toString();

          // Prefer idToken for AWS AppSync with Cognito User Pools auth
          // AppSync expects the raw JWT in the 'authorization' header (no Bearer prefix)
          if (idToken) {
            // Ensure lowercase header key that AppSync examples use
            config.headers.set('authorization', idToken);
          } else if (accessToken) {
            // Fallback for non-AppSync REST endpoints if ever used
            config.headers.set('Authorization', `Bearer ${accessToken}`);
          }

          // Add request timestamp for debugging
          const extendedConfig = config as ExtendedAxiosRequestConfig;
          extendedConfig.metadata = { startTime: Date.now() };

          return config;
        } catch (authError) {
          return config;
        }
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        // Log response time for debugging
        const config = response.config as ExtendedAxiosRequestConfig;
        const startTime = config.metadata?.startTime;
        if (startTime) {
          // eslint-disable-next-line no-console
          console.debug(
            `API call took ${
              Date.now() - startTime
            }ms: ${response.config.method?.toUpperCase()} ${
              response.config.url
            }`
          );
        }

        return response;
      },
      (error: AxiosError) => {
        const apiError = ApiClient.transformError(error);
        return Promise.reject(apiError);
      }
    );
  }

  /**
   * Transform Axios error to our API error format
   */
  private static transformError(error: AxiosError): ApiClientError {
    const apiError = error as ApiClientError;

    // Determine error type
    apiError.isNetworkError = !error.response;
    apiError.isAuthError =
      error.response?.status === 401 || error.response?.status === 403;

    // Extract API errors from response
    if (error.response?.data) {
      const responseData = error.response.data as any;

      if (responseData.errors) {
        apiError.apiErrors = responseData.errors;
      } else if (responseData.message) {
        apiError.apiErrors = [
          {
            code: responseData.code || 'UNKNOWN_ERROR',
            message: responseData.message,
          },
        ];
      }
    }

    // Add fallback error for network issues
    if (apiError.isNetworkError) {
      apiError.apiErrors = [
        {
          code: 'NETWORK_ERROR',
          message: 'Network error occurred. Please check your connection.',
        },
      ];
    }

    // Log the error
    // eslint-disable-next-line no-console
    console.error('API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.message,
      apiErrors: apiError.apiErrors,
    });

    return apiError;
  }

  /**
   * GET request
   */
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.get<ApiResponse<T>>(url, config);
    return (response.data as any).data || (response.data as T);
  }

  /**
   * POST request
   */
  async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.axiosInstance.post<ApiResponse<T>>(
      url,
      data,
      config
    );
    return (response.data as any).data || (response.data as T);
  }

  /**
   * PUT request
   */
  async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.axiosInstance.put<ApiResponse<T>>(
      url,
      data,
      config
    );
    return (response.data as any).data || (response.data as T);
  }

  /**
   * PATCH request
   */
  async patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.axiosInstance.patch<ApiResponse<T>>(
      url,
      data,
      config
    );
    return (response.data as any).data || (response.data as T);
  }

  /**
   * DELETE request
   */
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.delete<ApiResponse<T>>(
      url,
      config
    );
    return (response.data as any).data || (response.data as T);
  }

  /**
   * GraphQL request for AppSync
   */
  async graphql<T = any>(
    query: string,
    variables?: Record<string, any>
  ): Promise<T> {
    const response = await this.axiosInstance.post('', {
      query,
      variables,
    });

    // Handle GraphQL errors
    if (response.data.errors) {
      const graphqlError = new Error('GraphQL Error') as ApiClientError;
      graphqlError.apiErrors = response.data.errors.map((error: any) => ({
        code: error.extensions?.code || 'GRAPHQL_ERROR',
        message: error.message,
        field: error.path?.join('.'),
      }));
      throw graphqlError;
    }

    return response.data.data;
  }

  /**
   * Upload file with progress tracking
   */
  async uploadFile(
    url: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);

    return this.axiosInstance.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(progress);
        }
      },
    });
  }

  /**
   * Download file
   */
  async downloadFile(url: string, filename?: string): Promise<void> {
    try {
      const response = await this.axiosInstance.get(url, {
        responseType: 'blob',
      });

      // Create download link
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('File download failed:', error);
      throw error;
    }
  }
}

/**
 * Get API base URL from environment configuration
 */
function getApiBaseUrl(): string {
  try {
    // Get environment configuration
    const envConfig = getCurrentEnvironmentConfig();

    // Use the GraphQL URL from the current environment
    if (envConfig.appSyncUrl) {
      return envConfig.appSyncUrl;
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Could not get environment config:', error);
  }

  // Fallback to environment variable
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Try to get from amplify_outputs.json (load dynamically to avoid build errors)
  try {
    // Use dynamic import for JSON files in Vite
    const fallbackUrl =
      'https://i5ikxhkptvbktlr7agoht2mn4y.appsync-api.eu-west-1.amazonaws.com/graphql';
    return fallbackUrl;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Could not load amplify configuration:', error);
    // Use fallback for development
    const fallbackUrl =
      'https://i5ikxhkptvbktlr7agoht2mn4y.appsync-api.eu-west-1.amazonaws.com/graphql';
    return fallbackUrl;
  }
}

/**
 * Default API client instance
 */
// eslint-disable-next-line import/no-mutable-exports
let apiClientInstance = new ApiClient({
  baseURL: getApiBaseUrl(),
  timeout: 30000,
});

export const getApiClientInstance = () => apiClientInstance;

// For backwards compatibility, export the instance directly
// Note: This creates a reference at module load time
export const apiClient = getApiClientInstance();

/**
 * Recreate API client with new environment configuration
 * This is called when switching environments
 */
export function recreateApiClient(): void {
  try {
    const newBaseUrl = getApiBaseUrl();
    apiClientInstance = new ApiClient({
      baseURL: newBaseUrl,
      timeout: 30000,
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to recreate API client:', error);
    throw error;
  }
}

/**
 * Get current API client instance
 */
export function getApiClient(): ApiClient {
  return apiClientInstance;
}

/**
 * Create API client for testing
 */
export function createTestApiClient(
  baseURL: string = 'http://localhost:3000'
): ApiClient {
  return new ApiClient({
    baseURL,
    timeout: 5000,
  });
}

// eslint-disable-next-line import/no-default-export
export default apiClientInstance;
